import React, { useState, useEffect, useRef } from "react";
import { Button } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";

interface Option {
  value: number | string;
  label: string;
}

interface SingleSelectButtonGroupWithOtherProps {
  label: string;
  options: Option[];
  selectedValue: string | number;
  setSelectedValue: (value: string | number) => void;
  error?: string;
  showRadioButton?: boolean;
  allowCustomOption?: boolean;
  contextKey?: string;
  customOptionsMap?: Record<string, string[]>;
  setCustomOptionsMap?: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
}

const SingleSelectButtonGroupWithOther: React.FC<SingleSelectButtonGroupWithOtherProps> = ({
  label,
  options,
  selectedValue,
  setSelectedValue,
  error = "",
  showRadioButton = true,
  allowCustomOption = false,
  contextKey,
  customOptionsMap = {},
  setCustomOptionsMap = () => {},
}) => {
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [newCustomValue, setNewCustomValue] = useState("");
  const inputRef = useRef<HTMLFormElement>(null);

  const customOptions = contextKey ? customOptionsMap[contextKey] || [] : [];

  const updateCustomOptions = (updated: string[]) => {
    if (contextKey) {
      setCustomOptionsMap((prev) => ({
        ...prev,
        [contextKey]: updated,
      }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isAddingCustom &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        if (newCustomValue.trim()) {
          addCustomOption();
        } else {
          setIsAddingCustom(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAddingCustom, newCustomValue]);

  const addCustomOption = () => {
    const trimmed = newCustomValue.trim();
    if (
      trimmed &&
      !customOptions.includes(trimmed) &&
      !options.some((opt) => opt.label === trimmed)
    ) {
      updateCustomOptions([...customOptions, trimmed]);
      setSelectedValue(trimmed);
    }
    setNewCustomValue("");
    setIsAddingCustom(false);
  };

  // Merge default and custom options into one Option[]
  const allOptions: Option[] = [
    ...options,
    ...customOptions.map((opt) => ({ value: opt, label: opt })),
  ];

  return (
    <div className="col-span-1">
      <label className="block common-bold-label pb-[14px]">
        {label}
        {error && <div className="text-red-500 text-sm !my-0">{error}</div>}
      </label>

      <div className="flex flex-wrap gap-2 mt-2">
        {allOptions.map((option) => (
          <Button
            key={option.label}
            type="text"
            onClick={() => setSelectedValue(option.label)}
            style={{
              backgroundColor: selectedValue === option.label ? "#d1e9ff" : "transparent",
              border: `1px solid ${selectedValue === option.label ? "#53b1fd" : "#e4e7ec"}`,
              color: selectedValue === option.label ? "#1849a9" : "#1d2939",
            }}
            className="rounded-[30px] px-[20px] py-[6px] text-sm flex items-center gap-x-[10px]"
          >
            {showRadioButton && (
              <div
                className={`w-4 h-4 flex items-center justify-center rounded-full border ${
                  selectedValue === option.label ? "border-[#0152fb] bg-[#d1e9ff]" : "border-[#e4e7ec]"
                }`}
              >
                {selectedValue === option.label && (
                  <div className="w-2 h-2 bg-[#0152fb] rounded-full" />
                )}
              </div>
            )}
            {option.label}
          </Button>
        ))}

        {allowCustomOption &&
          (isAddingCustom ? (
            <form
              ref={inputRef}
              onSubmit={(e) => {
                e.preventDefault();
                addCustomOption();
              }}
              className="flex items-center"
            >
              <input
                value={newCustomValue}
                onChange={(e) => setNewCustomValue(e.target.value)}
                placeholder="Enter custom option"
                className="px-3 py-[6px] text-sm border border-gray-300 rounded-l-full focus:outline-none"
                autoFocus
              />
              <Button
                htmlType="submit"
                className="rounded-r-full common-primary-button h-[32px]"
              >
                Add
              </Button>
            </form>
          ) : (
            <Button
              type="dashed"
              className="flex items-center gap-1 border-dashed rounded-[30px] text-sm"
              onClick={() => setIsAddingCustom(true)}
            >
              <PlusCircleOutlined /> Add Other
            </Button>
          ))}
      </div>
    </div>
  );
};

export default SingleSelectButtonGroupWithOther;
